/**
 * Generic confirmation dialog using portal pattern.
 * Supports primary (blue) and danger (red) variants.
 * Locks scroll, ESC to close, backdrop click to close.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | React.ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  cancelLabel?: string;
  isLoading?: boolean;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmVariant = 'primary',
  cancelLabel,
  isLoading = false,
}: ConfirmDialogProps) {
  const { t, i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Lock body scroll and handle ESC key
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, isLoading]);

  // Focus management: focus the confirm button on open, restore focus on close.
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    confirmButtonRef.current?.focus();

    return () => {
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const resolvedCancelLabel = cancelLabel ?? t('cancel', { defaultValue: 'Cancel' });

  const confirmButtonClasses =
    confirmVariant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white'
      : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white';

  const dialogContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overscroll-contain">
      {/* Backdrop click to close */}
      <div
        className="absolute inset-0 cursor-pointer"
        aria-hidden="true"
        onClick={isLoading ? undefined : onClose}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        className="relative z-[1] w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-6">
          <h2
            id="confirm-dialog-title"
            className="text-lg font-semibold text-[var(--pv-color-primary)]"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 transition-colors hover:text-gray-600 cursor-pointer disabled:opacity-50"
            aria-label={t('closeDialog', { defaultValue: 'Close dialog' })}
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6" id="confirm-dialog-description">
          {typeof message === 'string' ? (
            <p className="text-gray-600">{message}</p>
          ) : (
            message
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            {resolvedCancelLabel}
          </button>
          <button
            type="button"
            ref={confirmButtonRef}
            onClick={onConfirm}
            disabled={isLoading}
            className={`
              px-4 py-2 text-sm font-medium rounded-lg
              focus:outline-none focus:ring-2 focus:ring-offset-2
              disabled:opacity-50 cursor-pointer
              ${confirmButtonClasses}
            `}
          >
            {isLoading ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin" aria-hidden="true" />
                {isRTL ? 'جاري التحميل…' : 'Loading…'}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(dialogContent, document.body);
  }

  return dialogContent;
}
